/**
 * Hard limits + structural validation for import / sync payloads.
 * Rejects oversized or malformed dumps before they hit IDB.
 */

import { WORKSPACE_LIMITS, type PortableAIProvider, type WorkspaceExport } from './types';

export class WorkspacePayloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WorkspacePayloadError';
  }
}

function assertString(v: unknown, field: string, max = WORKSPACE_LIMITS.maxStringField): string {
  if (typeof v !== 'string') {
    throw new WorkspacePayloadError(`Invalid ${field}: expected string`);
  }
  if (v.length > max) {
    throw new WorkspacePayloadError(
      `Invalid ${field}: exceeds ${max.toLocaleString()} character limit`,
    );
  }
  return v;
}

function assertFiniteNumber(v: unknown, field: string): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    throw new WorkspacePayloadError(`Invalid ${field}: expected number`);
  }
  return v;
}

/**
 * Validate and normalize a WorkspaceExport. Throws WorkspacePayloadError.
 * Call after JSON.parse / decrypt, before import.
 */
export function validateWorkspaceExport(
  raw: unknown,
  options?: { jsonByteLength?: number },
): WorkspaceExport {
  if (options?.jsonByteLength != null && options.jsonByteLength > WORKSPACE_LIMITS.maxJsonBytes) {
    throw new WorkspacePayloadError(
      `Snapshot too large (${(options.jsonByteLength / 1024 / 1024).toFixed(1)} MB). Max is ${WORKSPACE_LIMITS.maxJsonBytes / 1024 / 1024} MB.`,
    );
  }

  if (typeof raw !== 'object' || raw === null) {
    throw new WorkspacePayloadError('Invalid export: expected JSON object');
  }
  const obj = raw as Record<string, unknown>;

  if (obj.app && obj.app !== 'opendork') {
    throw new WorkspacePayloadError('Invalid export: not an OpenDork backup');
  }
  if (obj.format === 'encrypted') {
    throw new WorkspacePayloadError('Encrypted backup — provide passphrase to decrypt');
  }

  if (!Array.isArray(obj.projects)) {
    throw new WorkspacePayloadError('Invalid export: projects missing');
  }
  if (obj.projects.length > WORKSPACE_LIMITS.maxProjects) {
    throw new WorkspacePayloadError(
      `Too many projects (${obj.projects.length}). Max ${WORKSPACE_LIMITS.maxProjects}.`,
    );
  }

  const projects = obj.projects.map((p, i) => {
    if (typeof p !== 'object' || p === null) {
      throw new WorkspacePayloadError(`Invalid project at index ${i}`);
    }
    const pr = p as Record<string, unknown>;
    if (typeof pr.id !== 'string' || typeof pr.name !== 'string') {
      throw new WorkspacePayloadError(`Invalid project at index ${i}: id/name required`);
    }
    if (!Array.isArray(pr.contextItems)) {
      throw new WorkspacePayloadError(`Invalid project ${pr.id}: contextItems required`);
    }
    assertString(pr.name, `projects[${i}].name`, 2_000);
    if (typeof pr.instructions === 'string') {
      assertString(pr.instructions, `projects[${i}].instructions`);
    }
    return p;
  });

  const chatsRaw = Array.isArray(obj.chats) ? obj.chats : [];
  if (chatsRaw.length > WORKSPACE_LIMITS.maxChats) {
    throw new WorkspacePayloadError(
      `Too many chats (${chatsRaw.length}). Max ${WORKSPACE_LIMITS.maxChats}.`,
    );
  }
  const chats = chatsRaw.filter((c) => {
    if (typeof c !== 'object' || c === null) return false;
    const ch = c as Record<string, unknown>;
    if (typeof ch.id !== 'string') return false;
    const pid = ch.projectId;
    return pid === undefined || pid === null || typeof pid === 'string';
  });

  const messagesRaw = Array.isArray(obj.messages) ? obj.messages : [];
  if (messagesRaw.length > WORKSPACE_LIMITS.maxMessages) {
    throw new WorkspacePayloadError(
      `Too many messages (${messagesRaw.length}). Max ${WORKSPACE_LIMITS.maxMessages}.`,
    );
  }
  const messages = messagesRaw.filter((m) => {
    if (typeof m !== 'object' || m === null) return false;
    const msg = m as Record<string, unknown>;
    return (
      typeof msg.id === 'string' &&
      typeof msg.chatId === 'string' &&
      typeof msg.content === 'string' &&
      msg.content.length <= WORKSPACE_LIMITS.maxStringField
    );
  });

  let providers: PortableAIProvider[] | undefined;
  if (Array.isArray(obj.providers)) {
    if (obj.providers.length > WORKSPACE_LIMITS.maxProviders) {
      throw new WorkspacePayloadError(
        `Too many providers (${obj.providers.length}). Max ${WORKSPACE_LIMITS.maxProviders}.`,
      );
    }
    providers = obj.providers.filter((p): p is PortableAIProvider => {
      if (typeof p !== 'object' || p === null) return false;
      const pr = p as PortableAIProvider;
      return (
        typeof pr.id === 'string' &&
        typeof pr.name === 'string' &&
        typeof pr.baseUrl === 'string' &&
        Array.isArray(pr.models)
      );
    });
  }

  return {
    version: 1,
    exportedAt:
      typeof obj.exportedAt === 'number' && Number.isFinite(obj.exportedAt)
        ? obj.exportedAt
        : Date.now(),
    app: 'opendork',
    format: 'plain',
    settings: obj.settings as WorkspaceExport['settings'],
    projects: projects as WorkspaceExport['projects'],
    chats: chats as WorkspaceExport['chats'],
    messages: messages as WorkspaceExport['messages'],
    providers,
    defaultAiProvider:
      typeof obj.defaultAiProvider === 'string' ? obj.defaultAiProvider : undefined,
  };
}

/** Estimate publish size for UI warnings. */
export function estimateExportStats(data: {
  projects: unknown[];
  chats: unknown[];
  messages: unknown[];
  providers?: unknown[];
}): {
  projects: number;
  chats: number;
  messages: number;
  providers: number;
  approxBytes: number;
  warnSize: boolean;
  warnMessages: boolean;
} {
  const json = JSON.stringify(data);
  const approxBytes = json.length;
  return {
    projects: data.projects.length,
    chats: data.chats.length,
    messages: data.messages.length,
    providers: data.providers?.length ?? 0,
    approxBytes,
    warnSize: approxBytes >= WORKSPACE_LIMITS.warnPublishBytes,
    warnMessages: data.messages.length >= WORKSPACE_LIMITS.warnPublishMessages,
  };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Simple passphrase strength (no external deps). */
export function passphraseStrength(pass: string): {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  ok: boolean;
} {
  if (!pass) return { score: 0, label: 'Enter a passphrase', ok: false };
  if (pass.length < 8) return { score: 0, label: 'Too short (min 8)', ok: false };
  if (pass.length < 12) return { score: 1, label: 'Weak — use 12+ characters', ok: false };

  let score: 0 | 1 | 2 | 3 | 4 = 2;
  const hasLower = /[a-z]/.test(pass);
  const hasUpper = /[A-Z]/.test(pass);
  const hasDigit = /\d/.test(pass);
  const hasSymbol = /[^a-zA-Z0-9]/.test(pass);
  const classes = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;

  if (pass.length >= 16 && classes >= 3) score = 4;
  else if (pass.length >= 14 && classes >= 2) score = 3;
  else if (pass.length >= 12) score = 2;

  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'] as const;
  return {
    score,
    label: labels[score] || 'Fair',
    ok: pass.length >= 12,
  };
}
